import clsx from "clsx";
import { useEffect } from "react";
import { ContactDTO, ContactType, FindAllContactsFilters, FindContactsTargetResponse } from "../../../../../lib/filters/dto";
import { keycloak } from "../../../../../lib/keycloak";
import { Preferences } from "../../../../../lib/preferences";
import { PreferencesSection } from "../../../../../lib/preferences/dto";
import { useAppDispatch, useAppSelector } from "../../../../../lib/redux/hooks";
import { ConfirmModal } from "../../../../../ui/organisms/confirmModal.tsx";
import { NotFound } from "../../../../../ui/organisms/notFound";
import { Pagination } from "../../../../../ui/organisms/pagination";
import { EditCompany } from "../../../../database/companies/editCompany/index";
import {
  ContactStateEnum,
  TargetListDTO
} from "../../../../targetList/dto";
import { CustomerAccessEnum } from "../../../../users/dto";
import { ContactsCounterDTO } from "../../../contacts/dto";
import { OutcomeFilters } from "../../outcomeFilters";
import { TargetCompaniesTable } from "./table";
import { useRemoveContactsMutation } from "../../../../../../features/contactsApi";
import { addBannerWithTimeout } from "../../../../../lib/centralizedBanner/slice";
import { ProjectDTO } from "../../../../projects/dto";
import { EmailsMessagesDTO, FindAllCustomMailsResponseDTO } from "../../../../projects/mails/dto";
import { PersonDTO } from "../../../../database/people/dto";

interface Props {
  showPopup: boolean
  counters: ContactsCounterDTO
  setShowPopup: (action: boolean) => void
  selectedIds: { contactId: string, companyId: string }[]
  setSelectedIds: (action: { contactId: string, companyId: string }[]) => void
  filters: FindAllContactsFilters
  setFilters: (action: Partial<FindAllContactsFilters>) => void
  setPage: (action: number) => void
  resetFilters: () => void
  contacts: {
    total: number
    page: number
    perPage: number
    prev: boolean
    next: boolean
    totalPage: number
    data: { company: ContactDTO<ContactType.COMPANY>, employees: PersonDTO[] }[]
  }
  targetList: TargetListDTO
  project: ProjectDTO
  isLoadingEmailsHistory: boolean
  emailsHistory: { messages: EmailsMessagesDTO[] }
  templates: FindAllCustomMailsResponseDTO
  section: "target" | "target-bin"
}

export function TargetCompaniesList(props: Props) {
  const { showPopup, counters, setShowPopup, selectedIds, section, setSelectedIds, filters, setFilters, setPage, contacts, targetList, project, isLoadingEmailsHistory, emailsHistory, templates } = props
  const dispatch = useAppDispatch();
  const usersState = useAppSelector((state) => state.users);

  const [removeContacts, { isLoading: isRemoving }] = useRemoveContactsMutation();
  const handleRemoveContacts = async (ids: string[]) => {
    try {
      await removeContacts({ contacts: ids, targetList: targetList.id }).unwrap();
      setShowPopup(false);
      setSelectedIds([]);
      dispatch(addBannerWithTimeout({
        message: 'Contatti rimossi con successo.',
        variant: 'success'
      }))
    } catch (error) {
      setShowPopup(false);
      dispatch(addBannerWithTimeout({
        message: 'Si è verificato un errore durante la rimozione dei contatti.',
        variant: 'error'
      }))
    }
  }

  return (
    <div className="w-full" style={{ height: 'calc(100% - 64px - 48px)' }}>
      <div className={clsx("px-4 h-12 flex items-start justify-between border-b border-b-neutral-100", {
        "h-12": !filters.outcome.map(v => v.value).includes(ContactStateEnum.TORECALL),
        "h-[84px]": filters.outcome.map(v => v.value).includes(ContactStateEnum.TORECALL),
        hidden: keycloak.hasRealmRole('customer') && usersState.findMeResponse?.customerAccess !== CustomerAccessEnum.FULL
      })}>
        <div className={clsx("flex items-center gap-10", {})}>
          <OutcomeFilters
            isBin={window.location.pathname.includes('target-list-bins')}
            filters={filters}
            setFilters={setFilters}
            counters={counters?.dto}
          />
        </div>
      </div>
      {contacts.total === 0 ? (
        <div style={{ height: filters.outcome.map(v => v.value).includes(ContactStateEnum.TORECALL) ? 'calc(100% - 64px - 84px)' : 'calc(100% - 64px - 48px)' }}>
          <NotFound />
        </div>
      ) : (
        <TargetCompaniesTable
          isLoadingEmailsHistory={isLoadingEmailsHistory}
          emailsHistory={emailsHistory}
          contacts={contacts}
          selectedIds={selectedIds}
          setFilters={setFilters}
          filters={filters}
          setSelectedIds={arg => setSelectedIds(arg)}
          setShowPopup={arg => setShowPopup(arg)}
        />
      )}
      {
        contacts?.total !== 0 &&
        <Pagination
          itemsPerPage={filters.itemsPerPage}
          setItemsPerPage={value => setFilters({ ...filters, itemsPerPage: value })}
          found={contacts?.total || 0}
          key={"pagination-companies-bottom"}
          pages={contacts?.totalPage || 0}
          currentPage={contacts?.page || 0}
          setPage={(page) => {
            setPage(page);
          }}
        />}
      <Preferences section={PreferencesSection.targetListCompanyPreferences} key={PreferencesSection.targetListCompanyPreferences} />
      <EditCompany project={project} section={section} listIndex={1} targetList={targetList} emailsHistory={emailsHistory} templates={templates} />
      {/* <ConfirmModal
        title={"Elimina persona"}
        label={"Sei sicuro di voler eliminare questa persona?"}
        confirmAction={
          () => dispatch(deletePerson([companiesState.employeeToDelete!]))
        }
        onClose={() => dispatch(setEmployeeToDelete(undefined))}
        visible={companiesState.employeeToDelete !== undefined}
      /> */}
      <ConfirmModal
        loading={isRemoving}
        title={"Rimuovi contatti dalla lista"}
        label={
          "Sei sicuro di voler eliminare i contatti selezionati dalla lista?"
        }
        confirmAction={function (): void {
          handleRemoveContacts(selectedIds.map(id => id.contactId));
        }}
        onClose={function (): void {
          setSelectedIds([]);
          setShowPopup(false);
        }}
        visible={showPopup}
      />
    </div>
  );
}
